import { useState, useEffect, useCallback, useRef } from 'react';
import { Stack, Modal, IconButton, ProgressIndicator, TextField } from '@fluentui/react';
import { deleteCandidateReport, fetchCandidateReports, uploadCandidateReport } from '../../api/api';
import { ArrowDownloadRegular, LightbulbRegular, ArrowUploadRegular, InfoRegular } from '@fluentui/react-icons';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useIntl } from 'react-intl';

import styles from './CandidateReport.module.scss';
import ReportList, { ReportListHandle } from '../../components/ReportList/ReportList';
import TrafficLightModal from '../../components/Modals/TrafficLightModal';
import ReportToolBar from '../../components/ReportToolBar';
import CandidateReportModal from '../../components/Modals/CandidateReportModal';
import { FileWithCandidateDetails } from '../../types/FileWithCandidateDetails';
import { CandidateReportsForDeletion } from '../../api';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { getMarkdownFromTranslation } from '../../utilities/markdownUtils';
import { useAuthErrorContext } from '../../components/AuthErrorProvider';
import { DeleteButton } from '../../components/DeleteButton/DeleteButton';
import { DeleteDialog } from '../../components/DeleteDialog/DeleteDialog';
import { ReportStatus } from '../../types';
import type { ReportItem as ReportItemType } from '../../types';
import { ReportItem } from '../../components/ReportItem/ReportItem';
import { ProfilePhotoStack } from '../../components/ProfilePhotoStack/ProfilePhotoStack';
import { OrderByOption } from '../trafficlightreport/types';
import { MAX_TRAFFIC_LIGHT_REPORTS, MAX_UPLOAD_FILES } from '../../constants/reportLimits';

// Type definition for candidatereport_data to match the expected structure
interface CandidateReportData {
  candidateFirstName?: string;
  candidateLastName?: string;
  profilePhotoFileName?: string;
  companyApplied?: string;
  jobNumber?: string;
}

// Define a constant for the session storage key
const JOB_NUMBER_FILTER_STORAGE_KEY = 'candidateReport.jobNumberFilter';

const CandidateReport = () => {
  const [checkedItems, setCheckedItems] = useState<ReportItemType[]>([]);
  const [orderBy, setOrderBy] = useState({ property: 'createddate', desc: true });
  const [isTrafficLightModalOpen, setIsTrafficLightModalOpen] = useState(false);
  // Store the raw data from API, never displayed directly
  const [candidateReport, setCandidateReport] = useState<ReportItemType[]>([]);
  // The filtered data for display purposes only
  const [displayReports, setDisplayReports] = useState<ReportItemType[]>([]);
  // Initialize job number filter from session storage
  const [jobNumberFilter, setJobNumberFilter] = useState<string>(() => {
    return sessionStorage.getItem(JOB_NUMBER_FILTER_STORAGE_KEY) || '';
  });
  const [isCandidateReportModalOpen, setIsCandidateReportModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const navigate = useNavigate();
  const intl = useIntl();
  const [hasAuthError, setHasAuthError] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [trafficLightReports, setTrafficLightReports] = useState<any[]>([]);

  // Create a ref to the ReportList component
  const reportListRef = useRef<ReportListHandle>(null);

  // Helper function to apply filter to candidate reports
  const applyFilter = (reports: ReportItemType[], filter: string) => {
    if (!filter) {
      return reports;
    }

    return reports.filter(report => {
      const jobNumber = (report.details as any)?.candidatereport_data?.jobNumber || '';
      return jobNumber.toLowerCase().includes(filter.toLowerCase());
    });
  };

  // Save filter to session storage when it changes
  useEffect(() => {
    sessionStorage.setItem(JOB_NUMBER_FILTER_STORAGE_KEY, jobNumberFilter);
  }, [jobNumberFilter]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);

    // Add file count limit check
    if (selectedFiles.length > MAX_UPLOAD_FILES) {
      toast.error(intl.formatMessage({ id: 'candidateReport.error.file.count' }, { maxFiles: MAX_UPLOAD_FILES }));
      return;
    }

    const validFileTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    const invalidFiles = selectedFiles.filter(file => !validFileTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error(
        intl.formatMessage(
          { id: 'candidateReport.error.file.type' },
          {
            count: invalidFiles.length,
            types: '.pdf, .docx',
          },
        ),
      );
      return;
    }

    setSelectedFiles(selectedFiles);
    setIsCandidateReportModalOpen(true);
  };
  const { handleAuthError } = useAuthErrorContext();

  const handleUpload = async (filesWithCandidateDetails: FileWithCandidateDetails[]) => {
    try {
      const uploads = filesWithCandidateDetails.map(async v => {
        const result = await uploadCandidateReport(v, handleAuthError);
        if (result.error) {
          throw new Error(result.error);
        }
      });

      await Promise.all(uploads);
      await toast.success(
        intl.formatMessage({ id: 'candidateReport.upload.success' }, { count: filesWithCandidateDetails.length }),
      );
      setIsLoading(true);
      const response = await fetchCandidateReports(undefined, handleAuthError);
      if (!response.error) {
        const typedReports = (response.data ?? []).map(report => ({
          ...report,
          status: (report.status || 'Error') as ReportStatus,
        }));
        setCandidateReport(typedReports);
        // Apply filter before setting display reports
        setDisplayReports(applyFilter(typedReports, jobNumberFilter));
      }
    } catch (error) {
      toast.error(
        intl.formatMessage(
          { id: 'candidateReport.upload.error' },
          { error: error instanceof Error ? error.message : String(error) },
        ),
      );
    } finally {
      setIsCandidateReportModalOpen(false);
      setIsLoading(false);
      setCheckedItems([]);
      setSelectedFiles([]);
    }
  };

  //reload the reports when the orderBy changes
  useEffect(() => {
    if (!isLoading) {
      handleFetchReports();
    }
  }, [orderBy]);

  const handleDownload = async () => {
    const itemsToDownload =
      checkedItems.filter(x => x.status !== 'Processing').length > 0
        ? checkedItems.filter(x => x.status !== 'Processing')
        : displayReports.filter(x => x.status !== 'Processing');

    if (itemsToDownload.length === 1) {
      // Handle single file download
      const item = itemsToDownload[0];
      if (item.fullpath) {
        const response = await fetch(item.fullpath);
        const blob = await response.blob();
        saveAs(blob, item.filename);

        if (item.report_path && item.report_path != '') {
          const response = await fetch(item.report_path);
          const blob = await response.blob();
          saveAs(blob, `${item.filename}.txt`);
        }
      }
    } else {
      const zip = new JSZip();
      await Promise.all(
        itemsToDownload.map(async item => {
          if (item.fullpath) {
            const response = await fetch(item.fullpath);
            const blob = await response.blob();
            if (item.report_path && item.report_path != '') {
              const response = await fetch(item.report_path);
              const blob = await response.blob();
              zip.file(`${item.filename}.txt`, blob);
            }
            zip.file(item.filename, blob);
          }
        }),
      );

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `candidateReports_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.zip`);
    }
  };

  const handleDelete = () => {
    if (displayReports.length === 0) return;
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const itemsToDelete = checkedItems.length > 0 ? checkedItems : displayReports;
      if (itemsToDelete.length === 0) {
        toast.error(intl.formatMessage({ id: 'candidateReport.error.no.files.delete' }));
        return;
      }

      const filenames = itemsToDelete.map(item => item.filename);
      const candidateReportsForDeletion: CandidateReportsForDeletion = {
        messages: filenames,
      };

      await deleteCandidateReport(candidateReportsForDeletion, handleAuthError);
      setCheckedItems([]);

      // Clear the job filter after deletion
      setJobNumberFilter('');

      toast.success(intl.formatMessage({ id: 'candidateReport.delete.success' }, { count: itemsToDelete.length }));
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'candidateReport.delete.error' }));
    } finally {
      setIsDeleteDialogOpen(false);
      // Refresh the report list
      const response = await fetchCandidateReports(undefined, handleAuthError);
      if (!response.error) {
        const typedReports = (response.data ?? []).map(report => ({
          ...report,
          status: (report.status || 'Error') as ReportStatus,
        }));
        setCandidateReport(typedReports);
        // Apply filter before setting display reports
        setDisplayReports(applyFilter(typedReports, jobNumberFilter));
      }
    }
  };

  const handleCreateTrafficLightReportModal = () => {
    const reportsToUse = checkedItems.length > 0 ? checkedItems : displayReports;

    // Check if more than the maximum allowed reports are selected
    if (reportsToUse.length > MAX_TRAFFIC_LIGHT_REPORTS) {
      toast.error(
        intl.formatMessage(
          { id: 'trafficLightModal.error.tooManyReports' },
          { maxReports: MAX_TRAFFIC_LIGHT_REPORTS, selectedCount: reportsToUse.length },
        ),
      );
      return;
    }

    // Create a deep clone of the reports
    const clonedReports = [...reportsToUse];
    setTrafficLightReports(clonedReports);
    setIsTrafficLightModalOpen(true);
  };

  const handleTrafficLightModalClose = useCallback(() => {
    setIsTrafficLightModalOpen(false);
    setCheckedItems([]);
    setTrafficLightReports([]);

    // Clear checked items in the ReportList component
    if (reportListRef.current) {
      reportListRef.current.clearCheckedItems();
    }
  }, []);

  const handleInfoModal = () => {
    setIsInfoModalOpen(true);
  };

  const orderByOptions = [
    { key: 'createddate', text: 'Created Date', order: 'desc' },
    { key: 'filename', text: 'Name', order: 'asc' },
    { key: 'status', text: 'Status', order: 'asc' },
  ] as OrderByOption<any>[];

  useEffect(() => {
    setCheckedItems([]);
    const fetchInitialData = async () => {
      if (!isInitialLoading) return;
      try {
        const response = await fetchCandidateReports(undefined, handleAuthError);
        if (!response.error) {
          const typedReports = (response.data ?? []).map(report => ({
            ...report,
            status: (report.status || 'Error') as ReportStatus,
          }));
          setCandidateReport(typedReports);
        }
      } catch (error) {
        console.error('Failed to fetch reports:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [handleAuthError]);

  const handleFetchReports = useCallback(async () => {
    if (isInitialLoading) return; // Don't poll while initial loading is happening

    const response = await fetchCandidateReports(undefined, handleAuthError);
    if (!response.error) {
      const typedReports = (response.data ?? []).map(report => ({
        ...report,
        status: (report.status || 'Error') as ReportStatus,
      }));
      setCandidateReport(typedReports);
    }
  }, [handleAuthError, isInitialLoading]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    const fetchReports = async () => {
      if (hasAuthError || isLoading || isInitialLoading) return;
      setIsLoading(true);
      try {
        await handleFetchReports();
      } finally {
        setIsLoading(false);
      }
    };

    // Only start polling after initial load is complete
    if (!hasAuthError && !isInitialLoading) {
      interval = setInterval(fetchReports, 30000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [handleAuthError, hasAuthError, isInitialLoading]);

  const handleModalClose = (val: boolean) => {
    setIsCandidateReportModalOpen(val);
    if (!val) {
      setSelectedFiles([]);
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  // Apply filter when jobNumber filter changes
  useEffect(() => {
    setDisplayReports(applyFilter(candidateReport, jobNumberFilter));
  }, [candidateReport, jobNumberFilter]);

  const handleJobNumberFilterChange = useCallback((value: string) => {
    setJobNumberFilter(value);
  }, []);

  return (
    <>
      <div className={styles.container}>
        <Stack horizontal className={styles.chatRoot}>
          <Stack horizontal tokens={{ childrenGap: 20 }} style={{ width: '100%' }}>
            <Stack.Item grow={1} style={{ height: '100%' }}>
              <div className={styles.mainContent}>
                <>
                  {!candidateReport.length && !isInitialLoading && (
                    <p className={styles.emptyStateMessage}>
                      {intl.formatMessage({ id: 'candidateReport.empty.message' })}
                    </p>
                  )}

                  <div className={'d-flex gap-2 flex-wrap'}>
                    <div
                      style={{
                        marginRight: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <ReportToolBar orderByOptions={orderByOptions} setOrderBy={setOrderBy} />
                      <TextField
                        placeholder={intl.formatMessage({ id: 'candidateReport.filter.jobNumber.placeholder' })}
                        value={jobNumberFilter}
                        onChange={(event, newValue) => handleJobNumberFilterChange(newValue || '')}
                        styles={{
                          root: {
                            width: 200,
                            marginLeft: '8px',
                            alignSelf: 'center',
                          },
                        }}
                      />
                    </div>

                    <input
                      type="file"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      id="file-upload"
                      multiple
                      accept=".docx,.pdf"
                    />
                    <label htmlFor="file-upload" className="toolbar-button--upload">
                      <ArrowUploadRegular className="button-icon" /> Upload
                    </label>

                    {!!candidateReport.length && (
                      <button
                        type="button"
                        onClick={handleDownload}
                        className="toolbar-button--primary"
                        disabled={displayReports.filter(x => x.status !== 'Processing').length === 0}
                      >
                        <ArrowDownloadRegular className="button-icon" />
                        Download (
                        {checkedItems.length === 0
                          ? displayReports.filter(x => x.status !== 'Processing').length
                          : checkedItems.filter(x => x.status !== 'Processing').length}
                        )
                      </button>
                    )}

                    <DeleteButton
                      itemCount={checkedItems.length}
                      onClick={handleDelete}
                      totalCount={displayReports.length}
                      showAllOption={true}
                    />

                    {candidateReport.length > 0 && (
                      <button
                        type="button"
                        onClick={handleCreateTrafficLightReportModal}
                        className="toolbar-button--primary"
                        disabled={displayReports.filter(x => x.status !== 'Processing').length === 0}
                      >
                        <LightbulbRegular className="button-icon" />
                        Create Traffic Light Report (
                        {checkedItems.length === 0
                          ? displayReports.filter(x => x.status !== 'Processing').length
                          : checkedItems.filter(x => x.status !== 'Processing').length}
                        )
                      </button>
                    )}

                    <button type="button" onClick={handleInfoModal} className="toolbar-button--primary">
                      <InfoRegular className="button-icon" /> Help
                    </button>
                  </div>
                  {isLoading || isInitialLoading ? (
                    <div className={styles.progressContainer}>
                      <ProgressIndicator />
                    </div>
                  ) : (
                    <div className={styles.progressContainer}>
                      <ProgressIndicator percentComplete={0} />
                    </div>
                  )}

                  {/* Show no results message when filtered list is empty but original data exists */}
                  {!displayReports.length && candidateReport.length > 0 && !isInitialLoading && !isLoading && (
                    <p className={styles.emptyStateMessage}>
                      {intl.formatMessage({ id: 'candidateReport.filter.noResults' })}
                    </p>
                  )}

                  {/* Always render the ReportList with the filtered reports */}
                  <ReportList
                    ref={reportListRef}
                    orderBy={orderBy}
                    items={displayReports}
                    showPlaceholderIcon={false}
                    onChecked={checkedItems => {
                      setCheckedItems(checkedItems);
                    }}
                    renderContent={item => (
                      <ReportItem
                        filename={item.filename}
                        status={item.status}
                        details={{
                          profile_photos: item.details?.profile_photos,
                          photo_data: item.details?.photo_data,
                          candidatereport_data: item.details?.candidatereport_data,
                          processed: item.details?.processed,
                          warnings: item.warnings,
                        }}
                        progress={item.progress}
                        role={item.role}
                        remarks={item.remarks}
                        imageComponent={
                          <ProfilePhotoStack
                            report={{
                              metadata: {
                                profile_photos: item.details?.profile_photos,
                              },
                              details: {
                                photo_data: item.details?.photo_data,
                                thumbnail_path: item.details?.thumbnail_path,
                                candidatereport_data: {
                                  candidateName:
                                    item.details?.candidatereport_data?.candidateFirstName +
                                      ' ' +
                                      item.details?.candidatereport_data?.candidateLastName || 'Unknown',
                                  profilePhotoFileName: item.details?.candidatereport_data?.profilePhotoFileName,
                                },
                              },
                            }}
                          />
                        }
                      />
                    )}
                  />
                </>
              </div>
            </Stack.Item>
          </Stack>
          <TrafficLightModal
            checkedItems={trafficLightReports}
            isTrafficLightModalOpen={isTrafficLightModalOpen}
            setIsTrafficLightModalOpen={setIsTrafficLightModalOpen}
            onClose={handleTrafficLightModalClose}
          />
          <CandidateReportModal
            files={selectedFiles}
            isOpen={isCandidateReportModalOpen}
            setIsOpen={handleModalClose}
            onUpload={handleUpload}
          />
          <Modal isOpen={isInfoModalOpen} onDismiss={() => setIsInfoModalOpen(false)} isBlocking={true}>
            <div className={styles.infoModal}>
              <IconButton
                iconProps={{ iconName: 'Cancel' }}
                onClick={() => setIsInfoModalOpen(false)}
                className={styles.infoModalCloseButton}
              />
              <div dangerouslySetInnerHTML={getMarkdownFromTranslation(intl, 'candidateReport.info.title')} />
              <div dangerouslySetInnerHTML={getMarkdownFromTranslation(intl, 'candidateReport.info.content')} />
            </div>
          </Modal>
          <DeleteDialog
            hidden={!isDeleteDialogOpen}
            onDismiss={() => setIsDeleteDialogOpen(false)}
            onConfirm={confirmDelete}
            itemCount={checkedItems.length || displayReports.length}
            messageId="candidateReport.delete.confirmMessage"
            titleId="candidateReport.delete.confirmTitle"
          />
        </Stack>
      </div>
    </>
  );
};

export default CandidateReport;
